import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookingScreen } from '../liveBooking/BookingScreen';
import { BTN_PRIMARY } from '../buttonStyles';
import { TEXT_ACTION } from './surfaces';
import { RefundMissedSheet } from './RefundMissedSheet';
import { useToast } from '../../hooks/useToast';
import { liveLessonsErrorMessage, rebookLiveMissedLesson } from '../../services/liveLessons';
import { bookingWindowFrom } from '../../utils/bookingWindow';
import { whenLine } from '../../utils/liveCalendar';
import { firstNameOf } from '../../utils/liveLessonText';
import { markBookingsQuiet } from '../../utils/oneToast';
import type { CoachAvailabilitySlot, LiveCallBooking } from '../../types/liveCall';
import Sheet from '../Sheet';

/**
 * A single Live Video Lesson the coach missed, held for the player
 * (decisions 84, 91 and 95; docs/booking-contracts.md 4). The player: what
 * happened, Suggest New Times (any open time of the same coach, booked at no
 * charge) and a small Refund instead. The coach: that they missed it and the
 * player can pick a new time. Once resolved, both read where it went.
 */
export function MissedSingle({ b, isCoach, otherName, otherPhoto, tz, pane = false }: {
  b: LiveCallBooking;
  isCoach: boolean;
  otherName: string;
  otherPhoto?: string | null;
  tz: string;
  /** The coach's pane: the line alone, inside its status card. */
  pane?: boolean;
}) {
  const navigate = useNavigate();
  const [picking, setPicking] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const miss = b.missResolution!;
  const coachFirst = firstNameOf(isCoach ? b.coachName : otherName, 'Your coach');
  const playerFirst = firstNameOf(isCoach ? otherName : b.playerName, 'Your player');
  const interrupted = miss.reason === 'coach_interruption';
  const LINE = `${pane ? '' : 'mt-6 '}text-[17px] leading-[22px] tracking-[-0.43px] text-db-text-1`;

  if (miss.status === 'rebooked' && miss.newBookingId) {
    return (
      <p className={LINE} data-missed-single="rebooked">
        Booked again at no charge.{' '}
        <button type="button" onClick={() => navigate(`/live/${miss.newBookingId}`)} className={TEXT_ACTION}>Open the New Lesson</button>
      </p>
    );
  }
  if (miss.status === 'refunded') {
    return <p className={LINE} data-missed-single="refunded">{isCoach ? `${playerFirst} took a refund for this lesson.` : 'Refunded in full.'}</p>;
  }
  if (isCoach) {
    return (
      <p className={LINE} data-missed-single="pending">
        {interrupted ? 'Your connection dropped for over 5 minutes.' : 'You missed this lesson.'} {playerFirst} can pick a new time.
      </p>
    );
  }
  return (
    <div data-missed-single="pending">
      <p className={LINE}>
        {interrupted ? `${coachFirst}'s connection dropped.` : `${coachFirst} didn't join.`} Your lesson is held for you.
      </p>
      <button type="button" onClick={() => setPicking(true)} data-lesson-primary className={`${BTN_PRIMARY} mt-6 w-full`}>
        Suggest New Times
      </button>
      <div className="mt-3 text-center">
        <button type="button" onClick={() => setRefunding(true)} className={`${TEXT_ACTION} text-[15px] leading-[20px]`}>Refund instead</button>
      </div>
      {picking && <RebookScreen b={b} coachName={otherName} coachFirst={coachFirst} photo={otherPhoto} tz={tz} onClose={() => setPicking(false)} />}
      {refunding && <RefundMissedSheet bookingId={b.id} onClose={() => setRefunding(false)} />}
    </div>
  );
}

/** Suggest New Times: the coach's open times, the chosen one booked at no charge (rebookLiveMissedLesson). */
export function RebookScreen({ b, coachName, coachFirst, photo, tz, onClose }: {
  b: LiveCallBooking; coachName: string; coachFirst: string; photo?: string | null; tz: string; onClose: () => void;
}) {
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const [slot, setSlot] = useState<CoachAvailabilitySlot | null>(null);
  const [zone, setZone] = useState(tz);
  const [busy, setBusy] = useState(false);
  const book = async () => {
    if (!slot) return;
    setBusy(true);
    try {
      markBookingsQuiet();
      const { bookingId } = await rebookLiveMissedLesson({ bookingId: b.id, slotId: slot.id });
      showSuccess(`Booked for ${whenLine(slot.startAt, zone)}.`);
      navigate(`/live/${bookingId}`, { replace: true });
    } catch (err) {
      showError(liveLessonsErrorMessage(err, 'Could not book this time. Please try again.'));
      setBusy(false);
    }
  };
  // The sheet (components.md 10): Cancel and the title in its bar, the booking calendar inside.
  return (
    <Sheet title={"Suggest New Times"} onClose={onClose} busy={busy}>
      <BookingScreen
        coachId={b.coachId} offeringType="single_live_lesson" coachName={coachName} coachFirstName={coachFirst}
        photoUrl={photo} title="Suggest New Times"
        line={`Pick any open time with ${coachFirst}. It's booked at no charge.`}
        banner={(
          <p className="rounded-[26px] bg-white px-4 py-3.5 text-[15px] leading-[20px] text-db-text-1">
            This replaces your {whenLine(b.scheduledStartAt, zone)} lesson.
          </p>
        )}
        latestStartAt={bookingWindowFrom(new Date()).latestStartAt}
        timeZone={zone} onZoneChange={setZone}
        selected={slot} onSelect={setSlot}
        buttonLabel="Book Lesson" busy={busy}
        onSubmit={() => { void book(); }} onBack={onClose}
        inSheet
      />
    </Sheet>
  );
}
